import React, {useEffect} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {RadioButton, Tooltip} from '@optimaxdev/design-system';
import {FastTruckMini, Icon, Question} from '@optimaxdev/icons';
import {getCorrectDayTwoDayDelivery} from '@optimaxdev/utils';
import classNames from 'classnames';
import {useInView} from 'react-intersection-observer';

import {useFeature} from 'components/feature';
import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {TWO_DAYS_MODAL_OTHER, TWO_DAYS_MODAL_TOMORROW} from '../../constants/deliveryStep';

import s from './shippingTwoDaysMethodRedesign.scss';

export type PropsType = {
    code: string;
    isActive: boolean;
    deliveryPrice: string | JSX.Element;
    selectShippingMethod: () => void;
    method: ShippingMethodType;
};
const openChat = () => {
    if (window.adaEmbed) window.adaEmbed.toggle();
};

/**
 * Shipping Methods List for shipping methods list
 *
 * @constructor
 */
export const ShippingTwoDaysMethodRedesign: React.FC<PropsType> = ({
    code,
    deliveryPrice,
    isActive,
    method: {additionalData, description},
    selectShippingMethod,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {inView, ref} = useInView({
        threshold: 1,
        triggerOnce: true,
    });
    const twoDayDeliveryDay = additionalData && getCorrectDayTwoDayDelivery(additionalData?.day);
    const isDayTomorrow = twoDayDeliveryDay === 'tomorrow';

    const {isOneStepAvailable} = useIsOneStepAvailable();

    useEffect(() => {
        if (inView && twoDayDeliveryDay) {
            sendDesktopAnalyticsPerSession({
                event: CHECKOUT_DELIVERY_STEP,
                isInteraction: false,
                label: {
                    message: `1BD Shipping - ${isDayTomorrow ? 'Tomorrow' : 'Other'} - Showed`,
                },
            });
        }
    }, [inView, isDayTomorrow, twoDayDeliveryDay]);

    const sendTooltipAnalytics = (isVisible: boolean) => {
        if (isVisible && twoDayDeliveryDay) {
            sendDesktopAnalyticsPerSession({
                event: CHECKOUT_DELIVERY_STEP,
                label: {
                    message: `1BD Shipping - ${
                        isDayTomorrow ? 'Tomorrow' : 'Other'
                    } - Learn More - open`,
                },
            });
        }
    };

    const renderTooltip = () => (
        <Tooltip
            content={
                <div className={s.tooltip}>
                    {isDayTomorrow ? TWO_DAYS_MODAL_TOMORROW.FIRST : TWO_DAYS_MODAL_OTHER.FIRST}
                    <br />
                    <br />
                    {isDayTomorrow ? TWO_DAYS_MODAL_TOMORROW.SECOND : TWO_DAYS_MODAL_OTHER.SECOND}
                    <br />
                    <br />
                    Got any questions?{' '}
                    <button className={s.buttonTitle} onClick={openChat} type="button">
                        Start a Live Chat
                    </button>
                </div>
            }
            closeTrigger="hover"
            offset={23}
            onVisibilityChanged={sendTooltipAnalytics}
            openTrigger="hover"
            position="right"
            isHideOnScroll
        >
            <Icon className={s.iconHelp} icon={Question} />
        </Tooltip>
    );

    return (
        <div key={code} ref={ref}>
            <div
                className={classNames(s.title, {
                    [s.oneStepTitle]: isOneStepAvailable,
                    [s.titleFacelift]: isFacelift,
                })}
            >
                Get your order by tomorrow
                {renderTooltip()}
            </div>

            <button
                className={classNames(s.item, {
                    [s.active]: isActive,
                    [s.itemFacelift]: isFacelift,
                    [s.oneStep]: isOneStepAvailable,
                })}
                onClick={selectShippingMethod}
                type="button"
            >
                <RadioButton
                    isChecked={isActive}
                    size={isFacelift ? 'medium' : 'small'}
                    value={code}
                />
                <div className={classNames(s.description, {[s.facelift]: isFacelift})}>
                    <p className={s.methodTitle}>{description}</p>
                    <div className={s.highlight}>
                        <>
                            <Icon className={s.icon} icon={FastTruckMini} />
                            Next-Day Delivery
                        </>
                    </div>
                </div>
                <p className={classNames(s.price, {[s.facelift]: isFacelift})}>{deliveryPrice}</p>
            </button>
        </div>
    );
};
